





























import { computed, defineComponent, onMounted, PropType, ref, toRefs, watch } from '@vue/composition-api';
import { createLogger } from '@tt4b/ttam-utils';
import {
  AdgroupFormData,
  FullNormalFormData,
  InventoryFlow,
  PreviewMaterial,
  PRODUCT_SPECIFIC_TYPE,
  ProductItem,
  KeyInfo,
  KeyType,
  ResizeStrategy,
} from '@tt4b/ttam-enums';
import { CreateMode } from '@ad/tt_ads_common_enums';
import { StateEnum, useStore, useCarouselPreviewProductsApi, useAllowlist } from '@tt4b/ttam-hooks';
import CreativePreviewer from '../../components/common/creative-previewer';
import PreviewCta from './preview-cta-carousel.vue';

import { useGetCatalogTitle } from './hook';
import { UpsertImproveTaskParams, useImageSettingStore } from '../creative/image-settings-enhancements/hooks';
const logger = createLogger('TtamCatalogsPreviewCompose');

const name = 'ttam-catalog-carousel-preview-compose';
export default defineComponent({
  name,
  components: {
    CreativePreviewer,
    PreviewCta,
  },
  props: {
    creativeUuid: {
      type: String,
      required: true,
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      required: true,
    },
    creativeFormData: {
      type: Object as PropType<FullNormalFormData>,
      required: true,
      default: () => ({} as FullNormalFormData),
    },
    additionalInfo: {
      type: Object,
      default: () => ({}),
    },
    displayName: {
      type: String,
      default: '',
    },
    avatarIcon: {
      type: String,
      default: '',
    },
    actionLabel: {
      type: String,
      default: '',
    },
    createMode: {
      type: String,
      default: CreateMode.CREATE,
    },
  },
  setup(props, { emit }) {
    const { upsertImproveTask, getImproveTaskStatus } = useImageSettingStore();
    const previewKey = ref(0);
    const { adgroupData, creativeFormData, displayName, avatarIcon, actionLabel, creativeUuid } = toRefs(props);
    const showedProductsInPreview = ref([] as ProductItem[]);
    const searchedProducts = ref([] as ProductItem[]);
    const queryKey = ref('');
    const catalogId = computed(() => adgroupData.value.productPlatformId || '');
    const bcId = computed(() => adgroupData.value.catalogAuthorizedBc || '');
    const pSetId = computed(() => creativeFormData.value.productSetId || '');
    const productIds = computed(() => creativeFormData.value.dpaProducts || []);
    const dpaProductsList = computed(() => creativeFormData.value.dpaProductsList || []);
    const productSpecificType = computed(() => creativeFormData.value.productSpecificType || 0);
    const isLoading = ref(false);
    const page = ref(0);
    const handlePlay = () => {
      emit('carousel-image-playing', true);
    };
    const handlePause = () => {
      emit('carousel-image-playing', false);
    };
    const { state } = useStore();
    const aadvid = computed(() => state[StateEnum.baseInfo].value?.aadvid);
    const catalogWordList = computed(() => state[StateEnum.catalogLexiconList].data.value);

    const filterProductList = computed(() =>
      showedProductsInPreview.value?.filter(product => product.OuterID?.indexOf?.(queryKey.value) > -1),
    );
    const selectionList = computed(() =>
      searchedProducts.value?.filter(product => product?.OuterID?.indexOf?.(queryKey.value) > -1),
    );

    const improveTaskStatus = computed(() => getImproveTaskStatus(creativeUuid.value));
    const isGeneratingBackground = computed(() => improveTaskStatus.value.loading);

    const imageList = computed(() => {
      if (filterProductList?.value?.length === 0) {
        return [{ imageUrl: '' }];
      }
      const { result } = improveTaskStatus.value;
      return showedProductsInPreview?.value?.map(item => {
        const improvedItem = result.find(resultItem => resultItem.productId === item.productID);
        return {
          imageUrl: improvedItem?.imageUrl ?? item?.image?.url,
          productTag: item?.title,
        };
      });
    });
    const handleSKUSelection = query => {
      showedProductsInPreview.value = searchedProducts.value;
      const selectedItem = showedProductsInPreview.value?.filter(product => product.OuterID === query);
      const otherItem = showedProductsInPreview.value?.filter(product => product.OuterID !== query);
      const concatProducts = selectedItem.concat(otherItem);
      showedProductsInPreview.value = concatProducts;
      previewKey.value = previewKey.value + 1;
    };

    const { title: productLabel } = useGetCatalogTitle({
      adgroupData,
      creativeData: creativeFormData,
    });

    const config = computed(() => {
      const configVal = {
        // 写死TikTok就行
        apps: [InventoryFlow.TIKTOK],
        info: {
          name: displayName?.value,
          avatar: avatarIcon?.value,
        },
        material: {
          type: PreviewMaterial.TIKTOK_CATALOG_CAROUSEL,
          label: productLabel?.value || '',
          actionLabel: actionLabel?.value,
          media: imageList?.value,
        },
        otherInfo: {
          aadvid,
          isLoading: isLoading?.value,
        },
        // 非常魔幻，CatalogImage必须加这个空对象
        placementControl: {},
      };

      return configVal;
    });
    const onSearch = async query => {
      try {
        const { dpaProducts = [] } = await useCarouselPreviewProductsApi({
          catalogId: catalogId.value,
          bcId: bcId.value,
          pSetId: pSetId.value,
          offset: 0,
          outerId: query,
        });
        searchedProducts.value = dpaProducts;
        queryKey.value = query;
      } catch (error) {
        // isLoading.value = false;
      }
    };
    const getPreviewProducts = async () => {
      isLoading.value = true;
      try {
        const { dpaProducts = [] } = await useCarouselPreviewProductsApi({
          catalogId: catalogId.value,
          bcId: bcId.value,
          pSetId: pSetId.value,
          offset: page.value,
          productIds: productSpecificType.value === PRODUCT_SPECIFIC_TYPE.PRODUCTS ? productIds.value : [],
        });
        searchedProducts.value = dpaProducts;
        showedProductsInPreview.value = dpaProducts;
        emit('update-preview-products', showedProductsInPreview.value);
      } catch (e) {
        console.log(e);
      } finally {
        isLoading.value = false;
      }
    };
    const onRefresh = () => {
      page.value = page.value + 1;
      queryKey.value = '';
      getPreviewProducts();
    };

    const onUpdatePreviewProducts = () => {
      page.value = 0;
      queryKey.value = '';
      getPreviewProducts();
    };

    watch([productSpecificType, pSetId, productIds], () => {
      onUpdatePreviewProducts();
    });

    const { isVSACarouselOptimizedImagesWhite } = useAllowlist();
    const showImageSetting = computed(() => isVSACarouselOptimizedImagesWhite.value);
    const carouselImageInfo = computed(
      () =>
        creativeFormData.value.carouselImageInfo ?? {
          keyInfo: KeyInfo.WithoutKeyInfo,
          keyType: KeyType.Disabled,
          resizeStrategy: ResizeStrategy.Original,
        },
    );
    const upsertImproveTaskParams = computed<UpsertImproveTaskParams>(() => ({
      creativeUuid: creativeUuid.value,
      reqParams: {
        catalogId: catalogId.value,
        productIds: showedProductsInPreview.value.map(it => it.productID),
        carouselImageInfo: carouselImageInfo.value,
        adInfo: {
          advertiserId: aadvid.value,
        },
      },
    }));

    // 修改image optimization配置，重新执行
    watch(
      upsertImproveTaskParams,
      () => {
        if (!showImageSetting.value) {
          return;
        }
        if (isLoading.value) {
          return;
        }

        upsertImproveTask(upsertImproveTaskParams.value);
      },
      {
        deep: true,
        immediate: true,
      },
    );

    onMounted(() => {
      onUpdatePreviewProducts();
    });

    return {
      name,
      config,
      onSearch,
      isLoading,
      previewKey,
      onRefresh,
      handlePlay,
      handlePause,
      selectionList,
      catalogWordList,
      filterProductList,
      handleSKUSelection,
      isGeneratingBackground,
    };
  },
});
